import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";

import { useTracking } from "@local-logic/core/tracking";
import { useScoreSelection } from "~/sdks/LocalContent/hooks/useScoreSelection";
import { useMarkers } from "~/sdks/LocalContent/hooks/useMarkers";
import { useCommute } from "~/sdks/LocalContent/hooks/useCommute";

import useEffectOnce from "~/hooks/useEffectOnce";
import { useStateManagerContext } from "~/context/stateManager";
import { useLocation } from "~/hooks/useLocation";

import type { POI, School } from "@local-logic/client";
import type { MarkerPoint } from "@local-logic/maps";
import type { CommuteData } from "~/sdks/LocalContent/hooks/useCommute/types";

import { DEFAULT_TRAVEL_MODE } from "~/sdks/LocalContent/constants";

import { transformScoresMenuData } from "./utils/transformScoresMenuData";
import { getIdFromMarker } from "./utils/getIdFromMarker";
import type { TransformedScoresMenuData } from "./utils/transformScoresMenuData/types";

import {
  type LocalContentProps,
  type POISubcategory,
  TrackingPipelineEvents,
  AnalyticsEvents,
  CommuteOptions,
} from "./types";

interface Location {
  lat: LocalContentProps["options"]["lat"];
  lng: LocalContentProps["options"]["lng"];
}

export type DefaultContext = {
  isLoading: boolean;
  isScoresLoading: boolean;
  isMarkersLoading: boolean;
  isCommuteDataLoading: boolean;
  isCategoriesMenuOpen: boolean;
  isCommuteCalculatorOpen: boolean;
  distanceUnit: LocalContentProps["options"]["distanceUnit"];
  locale: "en" | "fr";
  location: Location;
  country: string;
  centerMarker?: {
    lat: number;
    lng: number;
    addressLabel?: string;
  };
  map: {
    mapProvider: LocalContentProps["options"]["mapProvider"];
    zoom: LocalContentProps["options"]["zoom"];
    pitch: LocalContentProps["options"]["pitch"];
    bearing: LocalContentProps["options"]["bearing"];
    cooperativeGestures: LocalContentProps["options"]["cooperativeGestures"];
  };
  scoresMenu: TransformedScoresMenuData;
  activeScore: ReturnType<typeof useScoreSelection>["activeScore"];
  activeScoreData: ReturnType<typeof useScoreSelection>["activeScoreData"];
  activeCategory: ReturnType<typeof useScoreSelection>["activeCategory"];
  activeCategoryData: ReturnType<
    typeof useScoreSelection
  >["activeCategoryData"];
  selectedPoiSubcategory: POISubcategory | null;
  activeMarker?: MarkerPoint;
  activeMarkerDetails: School | POI | null;
  scoresData?: ReturnType<typeof useScoreSelection>["scoresData"];
  markersData?: ReturnType<typeof useMarkers>["data"];
  commuteData: CommuteData | null;
  hideScoresList: string[];
  hideAllScores?: boolean;
  commuteOptions: CommuteOptions;
  handleToggleIsCategoriesMenuOpen: () => void;
  handleToggleIsCommuteCalculatorOpen: () => void;
  handleSelectScore: ReturnType<typeof useScoreSelection>["handleSelectScore"];
  handleSelectCategory: ReturnType<
    typeof useScoreSelection
  >["handleSelectCategory"];
  handleSelectPoiSubcategory: (subcategory: POISubcategory | null) => void;
  handleMarkerClick: (marker?: MarkerPoint) => void;
  handleCalculateCommute: (options: DefaultContext["commuteOptions"]) => void;
};

const LocalContentContext = React.createContext<DefaultContext>(
  {} as DefaultContext
);

const useLocalContent = () => useContext(LocalContentContext);

const LocalContentProvider = ({
  children,
}: {
  children: React.JSX.Element;
}) => {
  const { sdkState } = useStateManagerContext<LocalContentProps>();
  const tracking = useTracking();
  const [isCategoriesMenuOpen, setIsCategoriesMenuOpen] = useState(false);
  const [isCommuteCalculatorOpen, setIsCommuteCalculatorOpen] = useState(false);
  const [selectedPoiSubcategory, setSelectedPoiSubcategory] =
    useState<DefaultContext["selectedPoiSubcategory"]>(null);
  const [activeMarker, setActiveMarker] = useState<MarkerPoint | undefined>(
    undefined
  );
  const [commuteOptions, setCommuteOptions] = useState<
    DefaultContext["commuteOptions"]
  >({
    destination: undefined,
    commuteType: DEFAULT_TRAVEL_MODE,
    label: undefined,
  });

  const { data: location } = useLocation({
    lat: sdkState.options?.marker?.lat,
    lng: sdkState.options?.marker?.lng,
  });

  const { data: commuteData, isLoading: isCommuteDataLoading } = useCommute({
    mapProvider: sdkState.options?.mapProvider,
    mode: commuteOptions.commuteType,
    locale: sdkState.globalOptions?.locale || "en",
    units: sdkState.options?.distanceUnit,
    label: commuteOptions.label,
    origin: {
      lat: sdkState.options?.lat || 0,
      lng: sdkState.options?.lng || 0,
    },
    destination: commuteOptions.destination,
  });

  useEffectOnce(() => {
    const eventAttributes = {
      mapProviderHasCustomKey:
        typeof sdkState.options?.mapProvider?.key !== "undefined",
      mapProviderHasCustomTheme:
        typeof sdkState.options?.mapProvider?.theme !== "undefined",
      centerMarkerProvided: typeof sdkState.options?.marker !== "undefined",
      sidebarDefaultOpen: sdkState.options?.scoresMenu?.defaultOpen,
    };

    tracking.postEvent({
      eventType: TrackingPipelineEvents.SDK_VIEWED,
      eventAttributes: Object.entries(eventAttributes).map(([key, value]) => ({
        label: key,
        data: value,
      })),
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.SDK_VIEWED,
      eventAttributes,
    });
  });

  const handleToggleIsCategoriesMenuOpen = useCallback(() => {
    setIsCategoriesMenuOpen((prev) => {
      if (!prev) {
        setIsCommuteCalculatorOpen(false);
        setCommuteOptions({
          ...commuteOptions,
          label: undefined,
          destination: undefined,
        });
      }

      return !prev;
    });
  }, [commuteOptions]);

  const handleToggleIsCommuteCalculatorOpen = useCallback(() => {
    setIsCommuteCalculatorOpen((prev) => {
      if (!prev) {
        setIsCategoriesMenuOpen(false);
      } else {
        setCommuteOptions({
          ...commuteOptions,
          label: undefined,
          destination: undefined,
        });
      }

      return !prev;
    });
  }, [commuteOptions]);

  const handleSelectPoiSubcategory = useCallback(
    (subcategory: POISubcategory | null) => {
      if (selectedPoiSubcategory === subcategory) {
        return;
      }
      setSelectedPoiSubcategory(subcategory);
    },
    [selectedPoiSubcategory]
  );

  const handleMarkerClick = useCallback(
    (marker?: MarkerPoint) => {
      if (
        activeMarker?.latitude === marker?.latitude &&
        activeMarker?.longitude === marker?.longitude
      ) {
        setActiveMarker(undefined);
        return;
      }

      setActiveMarker(marker);

      if (isCategoriesMenuOpen) {
        handleToggleIsCategoriesMenuOpen();
      }
    },
    [activeMarker, isCategoriesMenuOpen]
  );

  const handleCalculateCommute = (
    options: DefaultContext["commuteOptions"]
  ) => {
    setCommuteOptions(options);
    handleMarkerClick();

    if (!isCommuteCalculatorOpen) {
      handleToggleIsCommuteCalculatorOpen();
    }
  };

  const sortedScoresMenu = useMemo(
    () => transformScoresMenuData(sdkState.options?.scoresMenu),
    [sdkState.options?.scoresMenu?.categories]
  );

  const {
    isLoading: isScoresLoading,
    scoresData,
    activeScore,
    activeScoreData,
    activeCategory,
    activeCategoryData,
    handleSelectScore,
    handleSelectCategory,
  } = useScoreSelection({
    scoresMenu: sortedScoresMenu,
    lat: sdkState?.options?.lat || 0,
    lng: sdkState?.options?.lng || 0,
  });

  const { isLoading: isMarkersLoading, data: markersData } = useMarkers({
    lat: sdkState?.options?.lat || 0,
    lng: sdkState?.options?.lng || 0,
    category: activeScore || undefined,
  });

  useEffect(() => {
    if (activeScore === null) {
      setSelectedPoiSubcategory(null);
    }
  }, [activeScore]);

  /**
   * This is used to determine which scores should be hidden in the UI.
   * It is mainly calculated here to avoid recalculating it in multiple components.
   */
  const hideScoresList = useMemo(() => {
    let tempHideScoresList: string[] = [];

    if (!sortedScoresMenu) return tempHideScoresList;

    sortedScoresMenu.categories.forEach(([, categoryValue]) => {
      if (categoryValue.hide || sdkState.options?.hideScores) {
        tempHideScoresList = tempHideScoresList.concat(
          categoryValue.scores.flatMap(([scoreKey]) => scoreKey)
        );
      } else {
        tempHideScoresList = tempHideScoresList.concat(
          categoryValue.scores
            .filter(([, scoreValue]) => scoreValue.hideScore)
            .map(([scoreKey]) => scoreKey)
        );
      }
    });

    return tempHideScoresList;
  }, [sortedScoresMenu, sdkState.options]);

  const activeMarkerDetails = useMemo(() => {
    if (
      !markersData ||
      !selectedPoiSubcategory ||
      !activeMarker ||
      activeMarker.markers.length !== 1
    ) {
      return null;
    }

    return (
      markersData[selectedPoiSubcategory]?.find(
        (marker) =>
          activeMarker.markers[0].id ===
          getIdFromMarker(marker.lat, marker.lng, marker.name)
      ) || null
    );
  }, [activeMarker, markersData, selectedPoiSubcategory]);

  const contextValue = useMemo(
    () => ({
      isLoading: isScoresLoading || isMarkersLoading,
      isScoresLoading,
      isMarkersLoading,
      isCommuteDataLoading,
      isCategoriesMenuOpen,
      isCommuteCalculatorOpen,
      locale: sdkState.globalOptions?.locale || "en",
      location: {
        lat: sdkState.options?.lat,
        lng: sdkState.options?.lng,
      },
      country: location?.country || "CAN",
      centerMarker: sdkState.options?.marker,
      distanceUnit: sdkState.options?.distanceUnit,
      map: {
        mapProvider: sdkState.options?.mapProvider,
        zoom: sdkState.options?.zoom,
        pitch: sdkState.options?.pitch,
        bearing: sdkState.options?.bearing,
        cooperativeGestures: sdkState.options?.cooperativeGestures,
      },
      scoresMenu: sortedScoresMenu,
      activeScore,
      activeScoreData,
      activeCategory,
      activeCategoryData,
      activeMarker,
      activeMarkerDetails,
      markersData,
      scoresData,
      selectedPoiSubcategory,
      hideScoresList,
      hideAllScores: sdkState.options?.hideScores,
      commuteData: commuteData || null,
      commuteOptions,
      handleToggleIsCategoriesMenuOpen,
      handleToggleIsCommuteCalculatorOpen,
      handleSelectScore,
      handleSelectCategory,
      handleSelectPoiSubcategory,
      handleMarkerClick,
      handleCalculateCommute,
    }),
    [
      isScoresLoading,
      isMarkersLoading,
      isCategoriesMenuOpen,
      isCommuteCalculatorOpen,
      isCommuteDataLoading,
      sdkState.globalOptions,
      sdkState.options,
      location,
      sortedScoresMenu,
      activeScore,
      activeScoreData,
      activeCategory,
      activeCategoryData,
      activeMarker,
      activeMarkerDetails,
      markersData,
      scoresData,
      selectedPoiSubcategory,
      commuteData,
      hideScoresList,
      commuteOptions,
      handleToggleIsCategoriesMenuOpen,
      handleToggleIsCommuteCalculatorOpen,
      handleSelectScore,
      handleSelectCategory,
      handleSelectPoiSubcategory,
      handleMarkerClick,
      handleCalculateCommute,
    ]
  );

  return (
    <LocalContentContext.Provider value={contextValue}>
      {children}
    </LocalContentContext.Provider>
  );
};

export { useLocalContent, LocalContentProvider, type Location };
